import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow, Button, Divider, Stack, CircularProgress, Grid } from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getApiUrl, getUploadUrl } from '../config/api';

const StudentStatementPage = () => {
    const { studentId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<any>(null);
    const [school, setSchool] = useState<any>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const userInfo = localStorage.getItem('userInfo');
                if (!userInfo) return;
                const token = JSON.parse(userInfo).token;
                const config = { headers: { Authorization: `Bearer ${token}` } };

                const res = await axios.get(getApiUrl(`parents/statement/${studentId}`), config);
                setData(res.data);

                if (res.data.school) {
                    setSchool(res.data.school);
                } else {
                    // Fallback to separate fetch if not in statement response
                    try {
                        const schoolRes = await axios.get(getApiUrl('school'), config);
                        setSchool(schoolRes.data);
                    } catch (err) {
                        console.log('School profile fetch failed, using defaults');
                    }
                }
            } catch (error) {
                console.error('Error fetching statement:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [studentId]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;
    if (!data) return <Box sx={{ p: 4 }}>Statement not found.</Box>;

    return (
        <Box sx={{
            p: { xs: 2, md: 4 },
            bgcolor: { xs: 'white', md: '#F4F7FE' },
            minHeight: '100vh',
            '@media print': { p: 0, bgcolor: 'white' }
        }}>
            {/* Header Actions */}
            <Stack direction="row" justifyContent="space-between" mb={{ xs: 2, md: 4 }} className="no-print">
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                    sx={{ fontWeight: 700, color: '#4A5568' }}
                >
                    Back
                </Button>
                <Button
                    variant="contained"
                    startIcon={<PrintIcon />}
                    onClick={() => window.print()}
                    sx={{ bgcolor: '#263B7C', borderRadius: '10px', px: 3, fontWeight: 700 }}
                >
                    Print / Download
                </Button>
            </Stack>

            <Paper sx={{
                p: { xs: 3, sm: 6, md: 8 },
                maxWidth: 1000,
                mx: 'auto',
                borderRadius: { xs: 0, md: '24px' },
                boxShadow: { xs: 'none', md: '0 10px 40px rgba(0,0,0,0.03)' },
                border: { xs: 'none', md: '1px solid #edf2f7' },
                '@media print': {
                    p: 0,
                    boxShadow: 'none',
                    borderRadius: 0,
                    border: 'none',
                    maxWidth: '100%'
                }
            }} className="print-area">

                {/* Header Section */}
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'center', sm: 'flex-start' }}
                    mb={6}
                    spacing={4}
                    sx={{ textAlign: { xs: 'center', sm: 'left' } }}
                >
                    <Box>
                        {school?.logo && (
                            <img
                                src={getUploadUrl(school.logo)}
                                alt="Logo"
                                style={{ height: 70, marginBottom: 20, objectFit: 'contain' }}
                            />
                        )}
                        <Typography variant="h4" fontWeight={900} color="#263B7C" sx={{ letterSpacing: -0.5 }}>
                            {school?.name || 'School Name'}
                        </Typography>
                        <Box sx={{ mt: 1.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                                {school?.address}
                                {'\n'}Phone: {school?.phone}
                                {'\n'}Email: {school?.email}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ textAlign: { xs: 'center', sm: 'right' }, pt: { sm: 2 } }}>
                        <Typography variant="h5" fontWeight={900} color="#263B7C" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                            Statement
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary" fontWeight={700}>
                            Account Summary
                        </Typography>
                        <Divider sx={{ my: 1.5, borderColor: '#edf2f7', display: { xs: 'none', sm: 'block' } }} />
                        <Typography variant="caption" sx={{ bgcolor: '#F4F7FE', p: '4px 12px', borderRadius: '20px', fontWeight: 800, color: '#263B7C' }}>
                            DATE: {new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                        </Typography>
                    </Box>
                </Stack>

                <Divider sx={{ mb: 6, borderColor: '#edf2f7' }} />

                {/* Student Details Grid */}
                <Box sx={{ mb: 6, p: 4, bgcolor: '#F8FAFC', borderRadius: '20px', border: '1px solid #edf2f7' }}>
                    <Grid container spacing={4}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Student Name</Typography>
                            <Typography variant="h6" fontWeight={800} color="#2D3748">{data.student.name}</Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 4 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Student ID</Typography>
                            <Typography variant="h6" fontWeight={800} color="#2D3748">{data.student.id}</Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 4 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Grade / Class</Typography>
                            <Typography variant="h6" fontWeight={800} color="#2D3748">{data.student.class}</Typography>
                        </Grid>
                    </Grid>
                </Box>

                {/* Ledger Table - Scrollable on mobile */}
                <Box sx={{ overflowX: 'auto', mb: 4, mx: { xs: -3, sm: 0 } }}>
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#263B7C', '@media print': { bgcolor: '#263B7C !important', printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' } }}>
                                <TableCell sx={{ color: 'white', fontWeight: 800, border: 0 }}>Date</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 800, border: 0 }}>Description</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 800, border: 0 }}>Reference</TableCell>
                                <TableCell align="right" sx={{ color: 'white', fontWeight: 800, border: 0 }}>Debit ($)</TableCell>
                                <TableCell align="right" sx={{ color: 'white', fontWeight: 800, border: 0 }}>Credit ($)</TableCell>
                                <TableCell align="right" sx={{ color: 'white', fontWeight: 800, border: 0 }}>Balance ($)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.ledger.map((row: any, idx: number) => (
                                <TableRow key={idx} sx={{ bgcolor: idx % 2 === 0 ? 'white' : '#F9FAFB' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#4A5568' }}>{new Date(row.date).toLocaleDateString()}</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#2D3748' }}>{row.description}</TableCell>
                                    <TableCell>
                                        <Typography variant="caption" sx={{ bgcolor: '#EDF2F7', p: '2px 8px', borderRadius: '4px', fontWeight: 700 }}>
                                            {row.reference}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, color: '#E53E3E' }}>
                                        {row.debit > 0 ? Number(row.debit).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-'}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, color: '#38A169' }}>
                                        {row.credit > 0 ? Number(row.credit).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-'}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 800, color: '#263B7C' }}>
                                        {Number(row.balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>

                {/* Final Summary Card */}
                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 4 }}>
                    <Box sx={{
                        p: 3,
                        bgcolor: '#263B7C',
                        color: 'white',
                        borderRadius: '16px',
                        minWidth: 280,
                        textAlign: 'right',
                        boxShadow: '0 10px 20px rgba(38, 59, 124, 0.15)',
                        '@media print': { bgcolor: '#263B7C !important', printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' }
                    }}>
                        <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5 }}>
                            Total Outstanding Balance
                        </Typography>
                        <Typography variant="h4" fontWeight={900}>
                            ${Number(data.totalOutstanding).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </Typography>
                    </Box>
                </Stack>

                {/* Document Information */}
                <Box sx={{ mt: 10, pt: 4, borderTop: '2px dashed #edf2f7', textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
                        This is a computer-generated official Statement of Account for {data.student.name}.
                        <br />
                        Generated on {new Date().toLocaleString()} • Powered by SquareEdge SSAS
                    </Typography>
                </Box>
            </Paper>

            <style>
                {`
                    @media print {
                        .no-print { display: none !important; }
                        body { background: white !important; margin: 0 !important; }
                        .print-area { width: 100% !important; margin: 0 !important; border: none !important; }
                        table { width: 100% !important; }
                        @page { margin: 1.5cm; }
                    }
                `}
            </style>
        </Box>
    );
};

export default StudentStatementPage;
